/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateTemplateRequest } from '../models/create-template-request';
import { MaterialTableRequestDto } from '../models/material-table-request-dto';
import { OperationResultDtoOfBoolean } from '../models/operation-result-dto-of-boolean';
import { OperationResultDtoOfTemplatePreviewDto } from '../models/operation-result-dto-of-template-preview-dto';
import { OperationResultDtoOfTemplateReadDto } from '../models/operation-result-dto-of-template-read-dto';
import { OperationResultDtoOfTemplateSendResultDto } from '../models/operation-result-dto-of-template-send-result-dto';
import { PagedResultDtoOfTemplateReadDto } from '../models/paged-result-dto-of-template-read-dto';
import { SendTemplateRequest } from '../models/send-template-request';
import { TemplatePreviewRequest } from '../models/template-preview-request';
import { TemplateReadDto } from '../models/template-read-dto';
import { UpdateTemplateRequest } from '../models/update-template-request';

@Injectable({
  providedIn: 'root',
})
export class TemplatesService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation templatesListTemplates
   */
  static readonly TemplatesListTemplatesPath = '/api/templates/list';

  /**
   * List templates.
   *
   * Returns a filtered, sorted page of message templates.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesListTemplates()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesListTemplates$Response(params: {
    body: MaterialTableRequestDto
  }): Observable<StrictHttpResponse<PagedResultDtoOfTemplateReadDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesListTemplatesPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PagedResultDtoOfTemplateReadDto>;
      })
    );
  }

  /**
   * List templates.
   *
   * Returns a filtered, sorted page of message templates.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesListTemplates$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesListTemplates(params: {
    body: MaterialTableRequestDto
  }): Observable<PagedResultDtoOfTemplateReadDto> {

    return this.templatesListTemplates$Response(params).pipe(
      map((r: StrictHttpResponse<PagedResultDtoOfTemplateReadDto>) => r.body as PagedResultDtoOfTemplateReadDto)
    );
  }

  /**
   * Path part for operation templatesGetTemplate
   */
  static readonly TemplatesGetTemplatePath = '/api/templates/{id}';

  /**
   * Get template.
   *
   * Returns one message template with its directly stored HTML.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesGetTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  templatesGetTemplate$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<TemplateReadDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesGetTemplatePath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<TemplateReadDto>;
      })
    );
  }

  /**
   * Get template.
   *
   * Returns one message template with its directly stored HTML.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesGetTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  templatesGetTemplate(params: {
    id: number;
  }): Observable<TemplateReadDto> {

    return this.templatesGetTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<TemplateReadDto>) => r.body as TemplateReadDto)
    );
  }

  /**
   * Path part for operation templatesUpdateTemplate
   */
  static readonly TemplatesUpdateTemplatePath = '/api/templates/{id}';

  /**
   * Update template.
   *
   * Replaces a template and stores its HTML directly.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesUpdateTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesUpdateTemplate$Response(params: {
    id: number;
    body: UpdateTemplateRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfTemplateReadDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesUpdateTemplatePath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfTemplateReadDto>;
      })
    );
  }

  /**
   * Update template.
   *
   * Replaces a template and stores its HTML directly.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesUpdateTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesUpdateTemplate(params: {
    id: number;
    body: UpdateTemplateRequest
  }): Observable<OperationResultDtoOfTemplateReadDto> {

    return this.templatesUpdateTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfTemplateReadDto>) => r.body as OperationResultDtoOfTemplateReadDto)
    );
  }

  /**
   * Path part for operation templatesDeleteTemplate
   */
  static readonly TemplatesDeleteTemplatePath = '/api/templates/{id}';

  /**
   * Delete template.
   *
   * Deletes a stored message template.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesDeleteTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  templatesDeleteTemplate$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<OperationResultDtoOfBoolean>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesDeleteTemplatePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfBoolean>;
      })
    );
  }

  /**
   * Delete template.
   *
   * Deletes a stored message template.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesDeleteTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  templatesDeleteTemplate(params: {
    id: number;
  }): Observable<OperationResultDtoOfBoolean> {

    return this.templatesDeleteTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfBoolean>) => r.body as OperationResultDtoOfBoolean)
    );
  }

  /**
   * Path part for operation templatesCreateTemplate
   */
  static readonly TemplatesCreateTemplatePath = '/api/templates';

  /**
   * Create template.
   *
   * Creates a template without SOAP-era Base64 encoding.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesCreateTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesCreateTemplate$Response(params: {
    body: CreateTemplateRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfTemplateReadDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesCreateTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfTemplateReadDto>;
      })
    );
  }

  /**
   * Create template.
   *
   * Creates a template without SOAP-era Base64 encoding.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesCreateTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesCreateTemplate(params: {
    body: CreateTemplateRequest
  }): Observable<OperationResultDtoOfTemplateReadDto> {

    return this.templatesCreateTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfTemplateReadDto>) => r.body as OperationResultDtoOfTemplateReadDto)
    );
  }

  /**
   * Path part for operation templatesPreviewTemplate
   */
  static readonly TemplatesPreviewTemplatePath = '/api/templates/{id}/preview';

  /**
   * Preview template.
   *
   * Merges an allowlisted set of member fields for one reporter.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesPreviewTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesPreviewTemplate$Response(params: {
    id: number;
    body: TemplatePreviewRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfTemplatePreviewDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesPreviewTemplatePath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfTemplatePreviewDto>;
      })
    );
  }

  /**
   * Preview template.
   *
   * Merges an allowlisted set of member fields for one reporter.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesPreviewTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesPreviewTemplate(params: {
    id: number;
    body: TemplatePreviewRequest
  }): Observable<OperationResultDtoOfTemplatePreviewDto> {

    return this.templatesPreviewTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfTemplatePreviewDto>) => r.body as OperationResultDtoOfTemplatePreviewDto)
    );
  }

  /**
   * Path part for operation templatesSendTemplate
   */
  static readonly TemplatesSendTemplatePath = '/api/templates/{id}/send';

  /**
   * Send template.
   *
   * Queues a personalized, idempotent template dispatch and reports integration warnings.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `templatesSendTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesSendTemplate$Response(params: {
    id: number;
    body: SendTemplateRequest
  }): Observable<StrictHttpResponse<OperationResultDtoOfTemplateSendResultDto>> {

    const rb = new RequestBuilder(this.rootUrl, TemplatesService.TemplatesSendTemplatePath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OperationResultDtoOfTemplateSendResultDto>;
      })
    );
  }

  /**
   * Send template.
   *
   * Queues a personalized, idempotent template dispatch and reports integration warnings.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `templatesSendTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  templatesSendTemplate(params: {
    id: number;
    body: SendTemplateRequest
  }): Observable<OperationResultDtoOfTemplateSendResultDto> {

    return this.templatesSendTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<OperationResultDtoOfTemplateSendResultDto>) => r.body as OperationResultDtoOfTemplateSendResultDto)
    );
  }

}
